import { useLocation } from 'preact-iso'
import { useMemo } from 'preact/hooks'
import { AlertTriangle, Check } from 'lucide-preact'
import type { WebCardDto } from '@/types/campaign'
import { getWebTypeMeta } from '@/constants/webTypes'
import { summariseOutstanding, isTitleOutstanding } from '@/constants/signOffReadiness'
import { LeadGoalBar } from '@/components'
import { formatDateOnly } from '@/utils/dateTimeFormatters'
import { shortAgo } from '@/utils/time'
import { routes } from '@/utils/routes'
import { DataGrid, type ColumnConfig } from '@/components/common/grids'
import type { WebQaSummaryDto } from '@/api/asset'
import { AssetQaChip } from '@/components/webs/AssetQaChip'
import { useAssetQaVisible } from '@/hooks/useAssetQaVisible'
import { BRAND_CLASSES } from '@/constants/theme'

interface CampaignWebsListViewProps {
    webs: WebCardDto[]
    campaignId: string
    workspaceId: string
    clientCompanyId: string
    /** What QA still owes per web, read once by the tab (ORB-889). Absent means nothing outstanding. */
    qaByWebId: Map<string, WebQaSummaryDto>
}

/**
 * Sort key for the QA column: Criticals, then open findings, then unverified checks.
 *
 * ⚑ A zero-padded string, not a number, because the ordering is lexicographic and must stay that
 * way. Any arithmetic key has to weight the three tiers against each other, and every weighting is
 * a guess that eventually collides — the version this replaced used `Criticals * 1000 + …`, which
 * both let unverified checks outrank a real finding and broke outright past 1000 of anything.
 * Padding compares tier by tier and never carries between them.
 *
 * ⚑ Each part is clamped to the pad width. Sorting is the one place a longer string would compare
 * as *smaller* ("1000000" before "999999"), so the width is the contract, not a guess at a maximum.
 * Six digits is far past anything a run can hold; the clamp is there so that stays true rather than
 * being assumed.
 */
const QA_SORT_PAD = 6
const QA_SORT_MAX = 10 ** QA_SORT_PAD - 1

const qaSortKey = (qa: WebQaSummaryDto): string =>
    [qa.CriticalFindings, qa.OpenFindings, qa.UnverifiedChecks]
        .map(count => String(Math.min(Math.max(count, 0), QA_SORT_MAX)).padStart(QA_SORT_PAD, '0'))
        .join('|')

const getLaunchStatus = (launchDate: string): { text: string; variant: 'past' | 'today' | 'future' } => {
    if (!launchDate) return { text: '—', variant: 'past' }

    // Get today's date as YYYY-MM-DD in local time
    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

    if (launchDate === todayStr) {
        return { text: 'Today', variant: 'today' }
    } else if (launchDate < todayStr) {
        return { text: formatDateOnly(launchDate), variant: 'past' }
    } else {
        return { text: formatDateOnly(launchDate), variant: 'future' }
    }
}

export function CampaignWebsListView({ webs, campaignId, workspaceId, clientCompanyId, qaByWebId }: CampaignWebsListViewProps) {
    /**
     * ⚑ The whole QA COLUMN goes, not just its cells (ORB-982). An empty cell here already means
     * "nothing outstanding", so leaving the column in place for a customer would print a header
     * asserting a judgement we are deliberately not showing them — and a column of blanks reads as
     * a rendering fault besides.
     */
    const qaVisible = useAssetQaVisible()
    const location = useLocation()

    const handleRowClick = (web: WebCardDto) => {
        const url = routes.scoped(clientCompanyId, workspaceId).web(campaignId, web.Id)
        location.route(url)
    }

    const columns = useMemo<ColumnConfig<WebCardDto>[]>(() => {
        const allColumns: ColumnConfig<WebCardDto>[] = [
        {
            key: 'title',
            header: 'Web Title',
            sortable: true,
            accessor: (web) => web.Title,
            render: (web) => (
                <div className="flex items-center gap-2">
                    {/* A web created before the content call has no title yet, and the API's GetTitle()
                        falls back to the internal reference name. Showing that in mono and marking it
                        keeps a planning code from reading as if it were the public title. */}
                    {isTitleOutstanding(web.OutstandingForSignOff) ? (
                        <span className="flex items-baseline gap-1.5 min-w-0">
                            <span className="font-mono text-[13px] text-slate-400 dark:text-slate-500 truncate">{web.Name}</span>
                            <span className="text-xs text-slate-400 dark:text-slate-500 flex-shrink-0">— title not set</span>
                        </span>
                    ) : (
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                            {web.Title}
                        </span>
                    )}
                    {web.ConferenceNotFound && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${BRAND_CLASSES.STATUS.SYNC_ERROR}`}>
                            Sync Error
                        </span>
                    )}
                </div>
            ),
        },
        {
            key: 'type',
            header: 'Type',
            sortable: true,
            accessor: (web) => web.WebType.toLowerCase(),
            render: (web) => {
                const typeMeta = getWebTypeMeta(web.WebType)
                const Icon = typeMeta.Icon
                const chipClass = typeMeta.chipClass
                const displayLabel = web.WebType.trim()
                return (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${chipClass}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {displayLabel}
                    </span>
                )
            },
        },
        {
            key: 'launchDate',
            header: 'Launch Date',
            sortable: true,
            // An unset LaunchDate parses to NaN, which compares equal to everything and scatters the
            // dateless webs through the default sort. Null instead, so the grid groups them at one end.
            accessor: (web) => web.LaunchDate ? new Date(web.LaunchDate).getTime() : null,
            render: (web) => {
                const launchStatus = getLaunchStatus(web.LaunchDate)
                return (
                    <div className={`text-sm ${
                        launchStatus.variant === 'today'
                            ? 'text-primary-600 dark:text-primary-400 font-semibold'
                            : launchStatus.variant === 'future'
                            ? 'text-slate-600 dark:text-slate-400'
                            : 'text-slate-500 dark:text-slate-500'
                    }`}>
                        {launchStatus.text}
                    </div>
                )
            },
        },
        {
            key: 'leads',
            header: 'Leads',
            sortable: true,
            accessor: (web) => web.LeadsCount || 0,
            render: (web) => {
                const goal = Math.max(0, web.LeadGoal || 0)
                const leads = Math.max(0, web.LeadsCount || 0)
                return (
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {leads.toLocaleString()} / {goal.toLocaleString()}
                    </div>
                )
            },
        },
        {
            key: 'progress',
            header: 'Progress',
            sortable: false,
            render: (web) => {
                const goal = Math.max(0, web.LeadGoal || 0)
                const leads = Math.max(0, web.LeadsCount || 0)
                return (
                    <div className="w-32">
                        <LeadGoalBar sourced={leads} goal={goal} />
                    </div>
                )
            },
        },
        {
            key: 'beforeSignOff',
            header: 'Before Sign-Off',
            sortable: true,
            // Undefined means the response predates this field, which is not the same as "nothing
            // outstanding" — sort those last rather than letting them read as ready.
            accessor: (web) => web.OutstandingForSignOff ? web.OutstandingForSignOff.length : Number.MAX_SAFE_INTEGER,
            render: (web) => {
                const outstanding = web.OutstandingForSignOff
                if (!outstanding) return null
                if (outstanding.length === 0) {
                    return (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 border border-green-200 dark:border-green-800">
                            <Check className="w-3 h-3 flex-shrink-0" />
                            Ready
                        </span>
                    )
                }
                return (
                    // Hovering names the items; clicking falls through to the row, which opens the web.
                    // In a list you are triaging, and the next thing you do is go work on the one that is
                    // behind — a popover you then have to navigate past is a wasted step.
                    <span
                        title={summariseOutstanding(outstanding)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                    >
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        {outstanding.length} to go
                    </span>
                )
            },
        },
        {
            key: 'qa',
            header: 'QA',
            sortable: true,
            // ⚑ Sorted by Criticals first, then open findings, then unverified checks: in a list you
            // are triaging, and a web with one Critical wants opening before a web with six Minors.
            // Absent means nothing outstanding, which sorts last (ORB-889).
            //
            // ⚑⚑ Strictly lexicographic, never arithmetic. The previous key was
            // `Criticals * 1000 + Open + Unverified`, which broke the ordering it documented twice
            // over: it added a count of things that are WRONG to a count of things that are UNKNOWN,
            // which is the one sum this feature forbids everywhere else, and it let a web with
            // several unverified checks outrank a web with a genuine open finding. The 1000 was also
            // load bearing and unowned — a run carrying 1000 findings collides across tiers.
            // Raised by Copilot on PR #143.
            accessor: (web) => {
                const qa = qaByWebId.get(web.Id)
                if (!qa) return null
                return qaSortKey(qa)
            },
            render: (web) => {
                const qa = qaByWebId.get(web.Id)
                // No green "Ready" counterpart to the column beside it, deliberately: that column is
                // computed for every web, while this read only returns work that is owed — so a blank
                // cell here means "nothing outstanding OR never checked", and a tick would claim more
                // than we know.
                if (!qa) return null
                return (
                    <AssetQaChip
                        openFindings={qa.OpenFindings}
                        criticalFindings={qa.CriticalFindings}
                        assetCount={qa.FlaggedAssets}
                    />
                )
            },
        },
        {
            key: 'lastLead',
            header: 'Last Lead',
            sortable: true,
            // null, not 0: epoch is a real 1970 date and would interleave with genuine ones. Same
            // policy as launchDate above.
            accessor: (web) => web.LastLeadCreatedDate ? new Date(web.LastLeadCreatedDate).getTime() : null,
            render: (web) => (
                <div className="text-sm text-slate-600 dark:text-slate-400">
                    {web.LastLeadCreatedDate ? shortAgo(web.LastLeadCreatedDate) : 'No leads'}
                </div>
            ),
        },
    // ⚑ qaByWebId is a dependency, not decoration. The QA column's accessor and render close over it,
    // and the map arrives from an async read after the first paint — with an empty list here the
    // memo keeps the closures built over the empty map, so the column stays blank forever and the
    // sort accessor stays 0. Raised by Copilot on PR #143.
        ]

        // ⚑ Filtered after the literal rather than inside it, and the literal keeps its explicit
        // annotation: chaining `.filter` straight onto the array dropped the contextual type from
        // useMemo's parameter, and every column's `(web) =>` accessor silently became `any`.
        return allColumns.filter(column => column.key !== 'qa' || qaVisible)
    }, [qaByWebId, qaVisible])

    return (
        <DataGrid
            title="Webs"
            data={webs}
            columns={columns}
            getRowKey={(web) => web.Id}
            onRowClick={handleRowClick}
            defaultSortKey="launchDate"
            defaultSortDirection="desc"
            emptyMessage="No webs found"
            pagination={true}
            pageSize={10}
        />
    )
}
